import React, { useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import type { FeaturedBadgeRuleGroup } from "../../../models/rulesFeaturedBadge";
import { formatEventScopeLabel } from "../configs/featuredBadgeTabConfig";
import {
  formatFeaturedBadgeEntriesSummary,
  formatFeaturedBadgeTenantLabel,
  groupFeaturedBadgeRulesByScope,
} from "../configs/featuredBadgeRuleHelpers";
import { useGetTenantsQuery } from "../../../store/services/tenants";
import {
  useGetEventTypesQuery,
  useGetEventSubTypesQuery,
} from "../../../store/services/events-types";
import { ActionsCell, ActiveToggleCell } from "./columns/StandardRulesColumnCells";
import {
  RulesHierarchicalFilters,
  RulesHierarchicalFilterState,
} from "./RulesHierarchicalFilters";
import type { FeaturedBadgeRule } from "../../../models/rulesFeaturedBadge";

interface BadgeRulesDataTableProps {
  rows: FeaturedBadgeRule[];
  loading?: boolean;
  filters: RulesHierarchicalFilterState;
  onFiltersChange: (next: RulesHierarchicalFilterState) => void;
  onEdit: (group: FeaturedBadgeRuleGroup) => void;
  onDelete: (group: FeaturedBadgeRuleGroup) => void;
  onToggleActive: (group: FeaturedBadgeRuleGroup) => void;
  isDeleting?: boolean;
  mutationsDisabled?: boolean;
}

const toTableRow = (group: FeaturedBadgeRuleGroup): FeaturedBadgeRuleGroup => group;

export const BadgeRulesDataTable: React.FC<BadgeRulesDataTableProps> = ({
  rows,
  loading,
  filters,
  onFiltersChange,
  onEdit,
  onDelete,
  onToggleActive,
  isDeleting,
  mutationsDisabled,
}) => {
  const tableHeader = <RulesHierarchicalFilters filters={filters} onChange={onFiltersChange} />;

  const { data: tenants } = useGetTenantsQuery({});
  const { data: eventTypes } = useGetEventTypesQuery();
  const { data: eventSubTypes } = useGetEventSubTypesQuery();

  const tenantNameById = useMemo(
    () => new Map((Array.isArray(tenants) ? tenants : []).map((tenant) => [tenant.id, tenant.provider] as const)),
    [tenants]
  );

  const typeNameById = useMemo(
    () => new Map((eventTypes ?? []).map((type) => [type.eventTypeId, type.eventTypeName] as const)),
    [eventTypes]
  );
  const subTypeNameById = useMemo(
    () =>
      new Map(
        (eventSubTypes ?? []).map(
          (subType) => [subType.eventSubTypeId, subType.eventSubTypeName] as const
        )
      ),
    [eventSubTypes]
  );

  const displayRows = useMemo(() => {
    const grouped = groupFeaturedBadgeRulesByScope(rows);

    return grouped.map((group) => ({
      ...group,
      tenantName: formatFeaturedBadgeTenantLabel(
        group.tenantId,
        group.tenantName,
        tenantNameById
      ),
      eventTypeName:
        group.eventTypeName ??
        (group.eventTypeId != null ? typeNameById.get(group.eventTypeId) : undefined),
      eventSubTypeName:
        group.eventSubTypeName ??
        (group.eventSubTypeId != null ? subTypeNameById.get(group.eventSubTypeId) : undefined),
      entriesSummary: formatFeaturedBadgeEntriesSummary(group.entries),
    }));
  }, [rows, tenantNameById, typeNameById, subTypeNameById]);

  return (
    <DataTable
      value={displayRows}
      loading={loading}
      header={tableHeader}
      dataKey="groupKey"
      paginator
      rows={10}
      rowsPerPageOptions={[5, 10, 25, 50]}
      stripedRows
      emptyMessage="Sin registros"
      tableStyle={{ minWidth: "50rem" }}
    >
      <Column
        header="Tenant"
        body={(group: FeaturedBadgeRuleGroup) => group.tenantName ?? "-"}
        sortable
        sortField="tenantName"
      />
      <Column
        header="Tipo / Subtipo"
        body={(group: FeaturedBadgeRuleGroup) => formatEventScopeLabel(group)}
        sortable
        sortField="eventTypeId"
      />
      <Column
        header="Etiquetas"
        body={(group: FeaturedBadgeRuleGroup & { entriesSummary?: string }) =>
          group.entriesSummary ?? formatFeaturedBadgeEntriesSummary(group.entries)
        }
      />
      <Column
        header="Estado"
        body={(group: FeaturedBadgeRuleGroup) => (
          <ActiveToggleCell
            row={toTableRow(group)}
            onToggle={() => onToggleActive(group)}
            disabled={mutationsDisabled}
          />
        )}
      />
      <Column
        header="Acciones"
        body={(group: FeaturedBadgeRuleGroup) => (
          <div className="flex gap-2">
            <ActionsCell
              row={toTableRow(group)}
              onEdit={() => onEdit(group)}
              disabled={mutationsDisabled}
            />
            <Button
              icon="pi pi-trash"
              severity="danger"
              rounded
              aria-label="Eliminar regla"
              onClick={() => onDelete(group)}
              disabled={isDeleting || mutationsDisabled}
            />
          </div>
        )}
      />
    </DataTable>
  );
};
